import React, { useEffect, useMemo, useState } from 'react';
import { Bell, Search, Filter, CheckCircle2, AlertTriangle, Info, Trash2, Check, Mail, Settings, Plus, Clock, X, Send, Users, CreditCard, ChevronRight, MoreVertical } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
interface AdminNotificationsCenterProps {
  success: (msg: string) => void;
}
// Generate large mock data
const generateMockNotifications = () => {
  const types = ['info', 'success', 'alert', 'error'];
  const categories = ['system', 'billing', 'users', 'security', 'marketing'];
  const titles = ['Новая жалоба на консультацию', 'Успешная оплата подписки', 'Сбой отправки email', 'Новый астролог ожидает верификации', 'Системное обновление', 'Подозрительная активность', 'Отчет сгенерирован', 'Новый отзыв', 'Превышен лимит API', 'Пользователь заблокирован'];
  const data = [];
  for (let i = 1; i <= 150; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const isRead = Math.random() > 0.3; // 70% read
    data.push({
      id: i,
      title: titles[Math.floor(Math.random() * titles.length)] + ` #${i}`,
      message: `Это детальное описание системного уведомления номер ${i}. Оно содержит важную информацию о произошедшем событии в категории ${category}. Пожалуйста, обратите внимание на детали и примите необходимые меры если требуется.`,
      type,
      category,
      date: i < 5 ? `${i * 10} мин назад` : i < 20 ? `${Math.floor(i / 2)} часов назад` : `${Math.floor(i / 10)} дней назад`,
      read: isRead,
      link: '#',
      metadata: {
        userId: `USR-${1000 + i}`,
        ip: `192.168.1.${i % 255}`,
        browser: 'Chrome 120.0'
      }
    });
  }
  return data;
};
const initialNotifications = generateMockNotifications();
export function AdminNotificationsCenter({
  success
}: AdminNotificationsCenterProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    read: 'all',
    category: 'all'
  });
  const [notifications, setNotifications] = useState(initialNotifications);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);
  const tabs = [{
    id: 'all',
    label: 'Все уведомления',
    icon: <Bell className="w-4 h-4" data-id="element-5918" />
  }, {
    id: 'unread',
    label: 'Непрочитанные',
    icon: <Mail className="w-4 h-4" data-id="element-5919" />
  }, {
    id: 'system',
    label: 'Системные',
    icon: <Settings className="w-4 h-4" data-id="element-5920" />
  }, {
    id: 'users',
    label: 'Пользователи',
    icon: <Users className="w-4 h-4" data-id="element-5921" />
  }, {
    id: 'billing',
    label: 'Финансы',
    icon: <CreditCard className="w-4 h-4" data-id="element-5922" />
  }, {
    id: 'security',
    label: 'Безопасность',
    icon: <AlertTriangle className="w-4 h-4" data-id="element-5923" />
  }];
  // Filtering logic
  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (activeTab === 'unread' && n.read) return false;
      if (activeTab !== 'all' && activeTab !== 'unread' && n.category !== activeTab) return false;
      if (filters.type !== 'all' && n.type !== filters.type) return false;
      if (filters.category !== 'all' && n.category !== filters.category) return false;
      if (filters.read !== 'all') {
        const isRead = !!n.read;
        if (filters.read === 'true' && !isRead) return false;
        if (filters.read === 'false' && isRead) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q) || n.id.toString().includes(q);
      }
      return true;
    });
  }, [notifications, activeTab, filters, searchQuery]);
  // Pagination logic
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const paginatedNotifications = filteredNotifications.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [activeTab, filters, searchQuery]);
  // Actions
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({
      ...n,
      read: true
    })));
    setSelectedIds(new Set());
    success('Все уведомления отмечены как прочитанные');
  };
  const markAsRead = (id: number) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const deleteNotification = (id: number) => {
    setNotifications(notifications.filter(n => n.id !== id));
    const newSelected = new Set(selectedIds);
    newSelected.delete(id);
    setSelectedIds(newSelected);
    success('Уведомление удалено');
  };
  // Bulk Actions
  const toggleSelection = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const toggleAllOnPage = () => {
    if (selectedIds.size === paginatedNotifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedNotifications.map(n => n.id)));
    }
  };
  const bulkMarkRead = () => {
    setNotifications(notifications.map(n => selectedIds.has(n.id) ? {
      ...n,
      read: true
    } : n));
    setSelectedIds(new Set());
    success(`${selectedIds.size} уведомлений прочитано`);
  };
  const bulkDelete = () => {
    setNotifications(notifications.filter(n => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
    success(`${selectedIds.size} уведомлений удалено`);
  };
  // Helpers
  const getIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-5924" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-rose-500" data-id="element-5925" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-5926" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" data-id="element-5927" />;
    }
  };
  const getBgColor = (type: string, read: boolean) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'alert':
        return 'bg-amber-50/50 border-amber-100';
      case 'error':
        return 'bg-rose-50/50 border-rose-100';
      case 'success':
        return 'bg-emerald-50/50 border-emerald-100';
      default:
        return 'bg-blue-50/50 border-blue-100';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'system':
        return 'bg-slate-100 text-slate-700';
      case 'billing':
        return 'bg-green-100 text-green-700';
      case 'users':
        return 'bg-blue-100 text-blue-700';
      case 'security':
        return 'bg-rose-100 text-rose-700';
      case 'marketing':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div className="space-y-6" data-id="element-5928">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5929">
        <div data-id="element-5930">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-5931">
            Центр уведомлений
          </h2>
          <p className="text-sm text-slate-500" data-id="element-5932">
            Управление системными оповещениями и рассылками (
            {notifications.length} всего)
          </p>
        </div>
        <div className="flex gap-3" data-id="element-5933">
          <Button variant="outline" icon={<Settings className="w-4 h-4" data-id="element-5935" />} data-id="element-5934">
            Настройки
          </Button>
          <Button variant="primary" icon={<Plus className="w-4 h-4" data-id="element-5937" />} onClick={() => setShowCreateModal(true)} data-id="element-5936">
            Создать рассылку
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-5938">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4" data-id="element-5939">
          <Card padding="sm" className="shadow-none border-slate-200" data-id="element-5940">
            <div className="space-y-1" data-id="element-5941">
              {tabs.map(tab => {
              const count = tab.id === 'all' ? notifications.length : tab.id === 'unread' ? notifications.filter(n => !n.read).length : notifications.filter(n => n.category === tab.id).length;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-5942">
                    <div className="flex items-center gap-2" data-id="element-5943">
                      {tab.icon}
                      <span data-id="element-5944">{tab.label}</span>
                    </div>
                    {count > 0 && <Badge variant={tab.id === 'unread' ? 'pro' : 'neutral'} size="sm" className="text-[10px]" data-id="element-5945">
                        {count > 99 ? '99+' : count}
                      </Badge>}
                  </button>;
            })}
            </div>
          </Card>

          <Card padding="sm" className="shadow-none border-slate-200 bg-slate-50" data-id="element-5946">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-5947">
              Статистика системы
            </h3>
            <div className="space-y-4" data-id="element-5948">
              <div data-id="element-5949">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-5950">
                  <span className="text-slate-500" data-id="element-5951">Доставляемость email</span>
                  <span className="font-bold text-slate-900" data-id="element-5952">98.5%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-5953">
                  <div className="bg-emerald-500 h-2 rounded-full" style={{
                  width: '98.5%'
                }} data-id="element-5954"></div>
                </div>
              </div>
              <div data-id="element-5955">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-5956">
                  <span className="text-slate-500" data-id="element-5957">
                    Открываемость (Open Rate)
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-5958">42.3%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-5959">
                  <div className="bg-indigo-500 h-2 rounded-full" style={{
                  width: '42.3%'
                }} data-id="element-5960"></div>
                </div>
              </div>
              <div data-id="element-5961">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-5962">
                  <span className="text-slate-500" data-id="element-5963">Критичные ошибки (24ч)</span>
                  <span className="font-bold text-rose-600" data-id="element-5964">12</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-5965">
                  <div className="bg-rose-500 h-2 rounded-full" style={{
                  width: '15%'
                }} data-id="element-5966"></div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4" data-id="element-5967">
          <Card padding="sm" className="shadow-none border-slate-200" data-id="element-5968">
            <div className="flex flex-col sm:flex-row justify-between gap-4" data-id="element-5969">
              <div className="relative flex-1 max-w-md" data-id="element-5970">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-5971" />
                <input type="text" placeholder="Поиск по ID, заголовку или тексту..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5972" />
              </div>
              <div className="flex gap-2" data-id="element-5973">
                <Button variant="outline" size="sm" icon={<Check className="w-4 h-4" data-id="element-5975" />} onClick={markAllAsRead} data-id="element-5974">
                  Прочитать все
                </Button>
                <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-5977" />} onClick={() => setShowFilters(!showFilters)} data-id="element-5976">
                  Фильтры
                </Button>
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in slide-in-from-top-2" data-id="element-5978">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5979">
                  <div data-id="element-5980">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-5981">
                      Тип важности
                    </label>
                    <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3" value={filters.type} onChange={e => setFilters({
                  ...filters,
                  type: e.target.value
                })} data-id="element-5982">
                      <option value="all" data-id="element-5983">Все типы</option>
                      <option value="info" data-id="element-5984">Инфо</option>
                      <option value="success" data-id="element-5985">Успех</option>
                      <option value="alert" data-id="element-5986">Внимание</option>
                      <option value="error" data-id="element-5987">Ошибка</option>
                    </select>
                  </div>
                  <div data-id="element-5988">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-5989">
                      Категория
                    </label>
                    <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3" value={filters.category} onChange={e => setFilters({
                  ...filters,
                  category: e.target.value
                })} data-id="element-5990">
                      <option value="all" data-id="element-5991">Все категории</option>
                      <option value="system" data-id="element-5992">Системные</option>
                      <option value="billing" data-id="element-5993">Финансы</option>
                      <option value="users" data-id="element-5994">Пользователи</option>
                      <option value="security" data-id="element-5995">Безопасность</option>
                    </select>
                  </div>
                  <div data-id="element-5996">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-5997">
                      Статус
                    </label>
                    <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3" value={filters.read} onChange={e => setFilters({
                  ...filters,
                  read: e.target.value
                })} data-id="element-5998">
                      <option value="all" data-id="element-5999">Все статусы</option>
                      <option value="true" data-id="element-6000">Прочитанные</option>
                      <option value="false" data-id="element-6001">Непрочитанные</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end" data-id="element-6002">
                  <Button variant="ghost" size="sm" onClick={() => setFilters({
                type: 'all',
                read: 'all',
                category: 'all'
              })} data-id="element-6003">
                    Сбросить фильтры
                  </Button>
                </div>
              </div>}
          </Card>

          {/* Bulk Actions Bar */}
          {selectedIds.size > 0 && <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center justify-between animate-in fade-in" data-id="element-6004">
              <div className="flex items-center gap-3" data-id="element-6005">
                <span className="text-sm font-bold text-indigo-900" data-id="element-6006">
                  Выбрано: {selectedIds.size}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-6007">
                <Button variant="outline" size="sm" icon={<Check className="w-4 h-4" data-id="element-6009" />} onClick={bulkMarkRead} data-id="element-6008">
                  Прочитать
                </Button>
                <Button variant="outline" size="sm" className="text-rose-600 hover:bg-rose-50 border-rose-200" icon={<Trash2 className="w-4 h-4" data-id="element-6011" />} onClick={bulkDelete} data-id="element-6010">
                  Удалить
                </Button>
              </div>
            </div>}

          {/* Notifications List */}
          <Card padding="none" className="shadow-none border-slate-200 overflow-hidden" data-id="element-6012">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-4" data-id="element-6013">
              <input type="checkbox" className="rounded border-slate-300 w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={paginatedNotifications.length > 0 && selectedIds.size === paginatedNotifications.length} onChange={toggleAllOnPage} title="Выбрать все на странице" data-id="element-6014" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-6015">
                {filteredNotifications.length} уведомлений найдено
              </span>
            </div>

            <div className="divide-y divide-slate-100" data-id="element-6016">
              {paginatedNotifications.length > 0 ? paginatedNotifications.map(notification => <div key={notification.id} className={`p-4 transition-colors flex gap-4 group ${getBgColor(notification.type, notification.read)} hover:bg-slate-50`} data-id="element-6017">
                    <div className="pt-1" data-id="element-6018">
                      <input type="checkbox" className="rounded border-slate-300 w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={selectedIds.has(notification.id)} onChange={() => toggleSelection(notification.id)} data-id="element-6019" />
                    </div>
                    <div className="mt-1 flex-shrink-0" data-id="element-6020">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-6021">
                      <div className="flex justify-between items-start mb-1" data-id="element-6022">
                        <div className="flex items-center gap-2" data-id="element-6023">
                          <h4 className={`text-sm font-bold truncate ${notification.read ? 'text-slate-700' : 'text-slate-900'}`} data-id="element-6024">
                            {notification.title}
                          </h4>
                          {!notification.read && <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" data-id="element-6025"></span>}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6026">
                          {!notification.read && <button onClick={() => markAsRead(notification.id)} className="p-1 text-slate-400 hover:text-indigo-600 rounded hover:bg-indigo-50 transition-colors" title="Отметить как прочитанное" data-id="element-6027">
                              <Check className="w-4 h-4" data-id="element-6028" />
                            </button>}
                          <button onClick={() => deleteNotification(notification.id)} className="p-1 text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors" title="Удалить" data-id="element-6029">
                            <Trash2 className="w-4 h-4" data-id="element-6030" />
                          </button>
                        </div>
                      </div>
                      <p className={`text-sm line-clamp-2 mb-2 ${notification.read ? 'text-slate-500' : 'text-slate-700'}`} data-id="element-6031">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-2" data-id="element-6032">
                        <div className="flex items-center gap-3" data-id="element-6033">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${getCategoryColor(notification.category)}`} data-id="element-6034">
                            {notification.category}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-6035">
                            <Clock className="w-3 h-3" data-id="element-6036" />
                            {notification.date}
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2 text-indigo-600 hover:bg-indigo-50" onClick={() => setSelectedNotification(notification)} data-id="element-6037">
                          Подробнее <ChevronRight className="w-3 h-3 ml-1" data-id="element-6038" />
                        </Button>
                      </div>
                    </div>
                  </div>) : <div className="text-center py-16 bg-white" data-id="element-6039">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-6040" />
                  <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-6041">
                    Нет уведомлений
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-6042">
                    По вашему запросу ничего не найдено. Попробуйте изменить
                    фильтры.
                  </p>
                </div>}
            </div>

            {/* Pagination inside card footer */}
            {totalPages > 1 && <div className="border-t border-slate-200 p-4 bg-white" data-id="element-6043">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6044" />
              </div>}
          </Card>
        </div>
      </div>

      {/* === MODALS === */}

      {/* Create Broadcast Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-6045">
          <Card className="w-full max-w-2xl shadow-2xl" data-id="element-6046">
            <div className="flex justify-between items-center mb-6" data-id="element-6047">
              <div data-id="element-6048">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-6049">
                  Создать системную рассылку
                </h3>
                <p className="text-sm text-slate-500" data-id="element-6050">
                  Отправка уведомлений пользователям платформы
                </p>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-6052" />} onClick={() => setShowCreateModal(false)} data-id="element-6051" />
            </div>

            <div className="space-y-4 mb-6" data-id="element-6053">
              <div data-id="element-6054">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6055">
                  Заголовок
                </label>
                <input type="text" placeholder="Важное обновление системы..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6056" />
              </div>

              <div data-id="element-6057">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6058">
                  Сообщение
                </label>
                <textarea rows={4} placeholder="Текст уведомления..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6059" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-6060">
                <div data-id="element-6061">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6062">
                    Аудитория
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6063">
                    <option data-id="element-6064">Все пользователи (12,450)</option>
                    <option data-id="element-6065">Только Астрологи (342)</option>
                    <option data-id="element-6066">Пользователи с подпиской (1,205)</option>
                    <option data-id="element-6067">Администраторы (8)</option>
                  </select>
                </div>
                <div data-id="element-6068">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" data-id="element-6069">
                    Тип уведомления
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-6070">
                    <option value="info" data-id="element-6071">Информационное (Синее)</option>
                    <option value="success" data-id="element-6072">Успех (Зеленое)</option>
                    <option value="alert" data-id="element-6073">Внимание (Желтое)</option>
                    <option value="error" data-id="element-6074">Критическое (Красное)</option>
                  </select>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4" data-id="element-6075">
                <div className="flex items-center gap-2 mb-2" data-id="element-6076">
                  <Info className="w-4 h-4 text-slate-400" data-id="element-6077" />
                  <span className="text-sm font-bold text-slate-700" data-id="element-6078">
                    Предпросмотр
                  </span>
                </div>
                <div className="bg-white p-3 rounded border border-slate-200 shadow-sm" data-id="element-6079">
                  <div className="flex gap-3" data-id="element-6080">
                    <div className="mt-0.5" data-id="element-6081">
                      <Info className="w-5 h-5 text-blue-500" data-id="element-6082" />
                    </div>
                    <div data-id="element-6083">
                      <div className="text-sm font-bold text-slate-900" data-id="element-6084">
                        Заголовок уведомления
                      </div>
                      <div className="text-xs text-slate-500 mt-1" data-id="element-6085">
                        Текст уведомления появится здесь...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-6086">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} data-id="element-6087">
                Отмена
              </Button>
              <Button variant="primary" icon={<Send className="w-4 h-4" data-id="element-6089" />} onClick={() => {
            setShowCreateModal(false);
            success('Рассылка успешно запущена');
          }} data-id="element-6088">
                Отправить (12,450)
              </Button>
            </div>
          </Card>
        </div>}

      {/* Notification Detail Modal */}
      {selectedNotification && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-6090">
          <Card className="w-full max-w-2xl shadow-2xl" data-id="element-6091">
            <div className="flex justify-between items-start mb-6" data-id="element-6092">
              <div className="flex gap-3" data-id="element-6093">
                <div className="mt-1 bg-slate-100 p-2 rounded-full" data-id="element-6094">
                  {getIcon(selectedNotification.type)}
                </div>
                <div data-id="element-6095">
                  <div className="flex items-center gap-2 mb-1" data-id="element-6096">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-6097">
                      {selectedNotification.title}
                    </h3>
                    <Badge variant="outline" size="sm" className="text-[10px] uppercase" data-id="element-6098">
                      ID: {selectedNotification.id}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-6099">
                    <span className="flex items-center gap-1" data-id="element-6100">
                      <Clock className="w-3 h-3" data-id="element-6101" /> {selectedNotification.date}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${getCategoryColor(selectedNotification.category)}`} data-id="element-6102">
                      {selectedNotification.category}
                    </span>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-6104" />} onClick={() => setSelectedNotification(null)} data-id="element-6103" />
            </div>

            <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 mb-6" data-id="element-6105">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-id="element-6106">
                {selectedNotification.message}
              </p>
            </div>

            {selectedNotification.metadata && <div className="mb-6" data-id="element-6107">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-6108">
                  Системные метаданные
                </h4>
                <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto" data-id="element-6109">
                  <pre className="text-xs text-emerald-400 font-mono" data-id="element-6110">
                    {JSON.stringify(selectedNotification.metadata, null, 2)}
                  </pre>
                </div>
              </div>}

            <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-6111">
              <Button variant="outline" className="text-rose-600 hover:bg-rose-50 border-rose-200" icon={<Trash2 className="w-4 h-4" data-id="element-6113" />} onClick={() => {
            deleteNotification(selectedNotification.id);
            setSelectedNotification(null);
          }} data-id="element-6112">
                Удалить
              </Button>
              <div className="flex gap-3" data-id="element-6114">
                <Button variant="outline" onClick={() => setSelectedNotification(null)} data-id="element-6115">
                  Закрыть
                </Button>
                {!selectedNotification.read && <Button variant="primary" icon={<Check className="w-4 h-4" data-id="element-6117" />} onClick={() => {
              markAsRead(selectedNotification.id);
              setSelectedNotification(null);
              success('Отмечено как прочитанное');
            }} data-id="element-6116">
                    Прочитано
                  </Button>}
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}